import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PROJECT_CONTEXT_TOKEN } from '../tokens/project-context.token';

import { environment } from '../../../../../../environments/environment';

/**
 * Adds withCredentials: true and the XSRF-TOKEN header to all requests going to the backend API.
 *
 * WHY we handle XSRF here instead of Angular's built-in withXsrfConfiguration:
 *   Angular's built-in XSRF interceptor has a same-origin check — it skips header injection for
 *   cross-origin requests. In local dev (frontend :4200 → backend :3001) this means the
 *   X-XSRF-TOKEN header is never sent, causing 403 CSRF_TOKEN_MISMATCH errors.
 *   By reading the cookie ourselves and injecting the header unconditionally for API URLs, we
 *   work correctly in all environments (local dev, staging, production).
 *
 * Cookie reading:
 *   The XSRF-TOKEN cookie is intentionally non-HttpOnly so JavaScript can read it.
 *   The cosmos_session cookie is HttpOnly and is NOT readable here — it is sent automatically
 *   by the browser because withCredentials: true is set.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private getCookieValue(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = request.url.startsWith(environment.API_URL);
    if (!isApiUrl) {
      return next.handle(request);
    }

    const headers: Record<string, string> = {};

    // Inject CSRF token for state-changing requests
    const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
    if (!SAFE_METHODS.has(request.method)) {
      const xsrfToken = this.getCookieValue('XSRF-TOKEN');
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }

    // Inject project context header when present in request context
    const projectId = request.context.get(PROJECT_CONTEXT_TOKEN);
    if (projectId) {
      headers['X-Project-Id'] = projectId;
    }

    request = request.clone({
      withCredentials: true,
      setHeaders: headers,
    });

    return next.handle(request);
  }
}
